<template>
  <div class="login-container">
    <div class="left-section">
      <div class="logo">
        <img src="https://peelocar.mojay.pro/assets/img/logoblanc.png" alt="Peelo Logo" />
      </div>
      <a href="https://peelo.chat" class="back-link">
        <span>Retour au site</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M5 12h14M12 5l7 7-7 7"/>
        </svg>
      </a>
      <div class="hero-content">
        <TransitionGroup name="fade-slide">
          <div v-for="(slide, index) in slides" :key="'slide-' + index" v-show="currentSlide === index" class="slide">
            <h2>{{ slide.title }}</h2>
            <p>{{ slide.description }}</p>
          </div>
        </TransitionGroup>
        <div class="dots">
          <span 
            v-for="(slide, index) in slides" 
            :key="'dot-' + index"
            :class="['dot', { active: currentSlideIndex === index }]"
            @click="setSlide(index)"
          ></span>
        </div>
      </div>
    </div>
    <div class="right-section">
      <div class="auth-container">
        <h1>Connexion</h1>
        
        <form @submit.prevent="loginUser" class="login-form">
          <div class="form-group">
            <label for="email">Email</label>
            <input 
              type="email" 
              id="email" 
              v-model="loginData.email" 
              placeholder="Entrez votre email"
              required
              :class="{ 'input-error': errorMessage }"
            />
          </div>
          
          <div class="form-group">
            <label for="password">Mot de passe</label>
            <input 
              type="password" 
              id="password" 
              v-model="loginData.password" 
              placeholder="Entrez votre mot de passe"
              required
              :class="{ 'input-error': errorMessage }"
            />
          </div>
          
          <div v-if="errorMessage" class="error-message">
            {{ errorMessage }}
          </div>
          
          <div class="forgot-password-container">
            <router-link to="/forgot-password" class="forgot-password-link">Mot de passe oublié ?</router-link>
          </div>
          
          <button type="submit" class="login-button" :disabled="isLoading">
            <span v-if="isLoading" class="loader"></span>
            <span v-else>Se connecter</span>
          </button>
        </form>

        <div class="auth-footer">
          <div class="separator">
            <span>ou</span>
          </div>
          
          <div class="register-prompt">
            <span>Pas encore de compte ?</span>
            <router-link to="/register" class="register-button">
              Créer un compte
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </router-link>
          </div>
        </div>
        
        
      </div>
    </div>
  </div>
</template>

<script>
import { useRouter } from "vue-router";
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { apiBaseUrl } from '@/config';
import axios from 'axios';

export default {
  name: 'UserLogin',
  setup() {
    const router = useRouter();
    const currentSlideIndex = ref(0);
    const currentSlide = ref(0);
    const isTransitioning = ref(false);
    const user = ref(null);
    const isLoading = ref(false);
    const errorMessage = ref('');
    
    const loginData = ref({
      email: '',
      password: ''
    });
    
    const slides = ref([
      {
        title: "Un copilote pour vos ventes",
        description: "Réactif, intelligent, toujours prêt à conclure"
      },
      {
        title: "Répondez à vos clients",
        description: "Où que vous soyez, à tout moment"
      },
      {
        title: "Lancez votre business",
        description: "Créez votre chatbot sans appli mobile"
      }
    ]);

    let slideInterval;

    const setSlide = (index) => {
      if (isTransitioning.value) return;
      isTransitioning.value = true;
      currentSlideIndex.value = index;
      currentSlide.value = index;
      setTimeout(() => {
        isTransitioning.value = false;
      }, 800);
    };

    const startSlideShow = () => {
      slideInterval = setInterval(() => {
        setSlide((currentSlideIndex.value + 1) % slides.value.length);
      }, 7000);
    };
    
    const loginUser = async () => {
      try {
        // Réinitialiser le message d'erreur
        errorMessage.value = '';
        isLoading.value = true;
        
        // Vérifier les champs obligatoires
        if (!loginData.value.email || !loginData.value.password) {
          errorMessage.value = 'Veuillez remplir tous les champs';
          isLoading.value = false;
          return;
        }
        
        // Préparer les données à envoyer
        const requestData = {
          email: loginData.value.email,
          password: loginData.value.password
        };
        
        
        
        // Envoyer la requête d'authentification
        const response = await axios.post(`${apiBaseUrl}/verify/user`, requestData);
        
        
        
        if (response.data && response.data.data) {
          // Stocker les informations de l'utilisateur
          const userData = {
            id: response.data.data.id,
            name: response.data.data.name,
            email: response.data.data.email,
            tel: response.data.data.tel,
            profile_picture: response.data.data.profile_picture,
            provenance: response.data.data.provenance || 'email',
            pointing_chatbot_stats: response.data.data.pointing_chatbot_stats,
            token: response.data.token
          };
          
          localStorage.setItem('user', JSON.stringify({ data: userData }));
          
          // Rediriger vers le tableau de bord
          router.push('/dashboard');
        } else {
          throw new Error('Données utilisateur invalides dans la réponse');
        }
      } catch (error) {
        console.error('Erreur de connexion:', error);
        
        // Gérer les différents types d'erreurs
        if (error.response) {
          // Le serveur a répondu avec un statut d'erreur
          switch (error.response.status) {
            case 400:  
              errorMessage.value = 'Email ou mot de passe manquant';
              break;
            case 401:
              errorMessage.value = 'Mot de passe incorrect';
              break;
            case 404:
              errorMessage.value = 'Utilisateur introuvable';
              break;
            default:
              errorMessage.value = error.response.data?.error || 'Erreur lors de la connexion';
          }
        } else if (error.request) {
          // La requête a été faite mais aucune réponse n'a été reçue
          errorMessage.value = 'Impossible de joindre le serveur. Veuillez réessayer plus tard.';
        } else {
          // Une erreur s'est produite lors de la configuration de la requête
          errorMessage.value = 'Une erreur est survenue. Veuillez réessayer.';
        }
      } finally {
        isLoading.value = false;
      }
    };

    onMounted(() => {
      startSlideShow();
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          user.value = parsedUser.data;
          if (user.value) {
            router.push('/dashboard');
          }
        } catch (error) {
          console.error('Error parsing stored user data:', error);
          localStorage.removeItem('user');
        }
      }
    });

    onBeforeUnmount(() => {
      clearInterval(slideInterval);
    });

    return {
      router,
      currentSlideIndex,
      currentSlide,
      slides,
      setSlide,
      apiBaseUrl,
      user,
      loginData,
      loginUser,
      isLoading,
      errorMessage
    };
  }
};
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1fr;
  background-color: #1a1a1a;
  overflow: hidden;
}

.left-section {
  position: relative;
  overflow: hidden;
  border-radius: 24px;
  margin: 1rem;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 2rem);
  max-height: calc(100vh - 2rem);
}

.left-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('@/assets/peelochat_ecommerce.png');
  background-size: cover;
  background-position: center;
  transition: transform 0.8s ease;
  z-index: 0;
}

.left-section:hover::before {
  transform: scale(1.1);
}

.left-section::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, rgba(37, 211, 102, 0.6), rgba(37, 211, 102, 0.2));
  z-index: 1;
}

.left-section > * {
  position: relative;
  z-index: 2;
}

.logo {
  height: 48px;
}

.logo img {
  height: 100%;
  object-fit: contain;
}

.back-link {
  position: absolute;
  top: 2rem;
  right: 2rem;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(18, 140, 126, 0.2);
  border-radius: 20px;
  font-size: 14px;
  transition: background-color 0.2s;
}

.back-link:hover {
  background: rgba(18, 140, 126, 0.3);
}

.hero-content {
  margin-top: auto;
  margin-bottom: 3rem;
  color: white;
  position: relative;
  height: auto;
  min-height: 220px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  overflow: visible;
}

.slide {
  width: 100%;
  overflow: visible;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.slide h2 {
  font-size: 2.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.slide p {
  font-size: 1.2rem;
  line-height: 1.5;
  opacity: 0.9;
  margin-bottom: 2rem;
}

/* Enhanced transitions */
.fade-slide-enter-active {
  transition: all 0.8s ease-out;
  transition-delay: 0.2s;
}

.fade-slide-leave-active {
  transition: all 0.6s ease-in;
  position: absolute;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(30px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}

.fade-slide-move {
  transition: transform 0.8s ease;
}

.dots {
  display: flex;
  gap: 0.8rem;
  margin-top: 140px;
  justify-content: center;
}

.dot {
  width: 2.5rem;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  cursor: pointer;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.dot:hover {
  background: rgba(255, 255, 255, 0.4);
}

.dot.active {
  background: white;
  width: 4rem;
}

.right-section {
  padding: 2rem 4rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow-y: auto;
  overflow-x: hidden;
}

.auth-container {
  max-width: 400px;
  width: 100%;
  overflow: hidden;
}

h1 {
  color: white;
  font-size: 2.5rem;
  font-weight: 500;
  margin-bottom: 2.5rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.login-text {
  color: #8f8f8f;
  margin-bottom: 2rem;
}

.login-link {
  color: white;
  text-decoration: none;
  font-weight: 500;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  color: white;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.form-group input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: transparent;
  color: white;
  font-size: 1rem;
}

.form-group input:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.3);
}

.input-error {
  border-color: #ff5252 !important;
}

.error-message {
  color: #ff5252;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: rgba(255, 82, 82, 0.1);
  border-radius: 8px;
  border-left: 3px solid #ff5252;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.login-button {
  width: 100%;
  padding: 0.75rem;
  border: none;
  border-radius: 8px;
  background: white;
  color: #1a1a1a;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-button:hover {
  background: rgba(255, 255, 255, 0.9);
  transform: translateY(-1px);
}

.login-button:active {
  transform: translateY(0);
}

.login-button:disabled {
  background: rgba(255, 255, 255, 0.5);
  cursor: not-allowed;
}

.loader {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(26, 26, 26, 0.3);
  border-radius: 50%;
  border-top-color: #1a1a1a;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.terms {
  margin-top: 2rem;
  font-size: 0.875rem;
  color: #8f8f8f;
  text-align: center;
}

.terms a {
  color: white;
  text-decoration: none;
  font-weight: 500;
}

.terms a:hover {
  text-decoration: underline;
}

@media (max-width: 768px) {
  .login-container {
    grid-template-columns: 1fr;
    overflow-y: auto;
  }

  .right-section {
    padding: 2rem;
    min-height: 100vh;
  }

  .auth-container {
    margin: 0 auto;
    padding: 0 1rem;
  }

  h1 {
    font-size: 2rem;
  }

  .slide h2 {
    font-size: 2rem;
  }
}

.auth-footer {
  margin-top: 2rem;
}

.separator {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 2rem 0;
  color: #8f8f8f;
}

.separator::before,
.separator::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.separator span {
  padding: 0 10px;
}

.register-prompt {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin: 1.5rem 0;
}

.register-prompt span {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.95rem;
}

.register-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: rgba(37, 211, 102, 0.1);
  border-radius: 8px;
  color: #25D366;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.register-button:hover {
  background: rgba(37, 211, 102, 0.2);
  transform: translateX(5px);
}

.register-button svg {
  transition: transform 0.2s ease;
}

.register-button:hover svg {
  transform: translateX(3px);
}

.login-form {
  margin-top: 1rem;
}

.forgot-password-container {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1.5rem;
}

.forgot-password-link {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  transition: color 0.2s ease;
}

.forgot-password-link:hover {
  color: #25D366;
  text-decoration: underline;
}
</style>
